import type { FormAccessToken, FormSubmissionAnswer, PortalFormRequest, PortalFormRequestStatus, TenantForm } from '~/types/models';

export function emailAccessTokenToPortalFormRequest(options: {
    access: FormAccessToken;
    tenantId: string;
    form?: Pick<TenantForm, 'name' | 'slug' | 'kind' | 'description' | 'introText'> | null;
}): PortalFormRequest | null {
    const access = options.access;
    const accessTokenId = String(access.id || access.token || '').trim();
    const formId = String(access.formId || '').trim();
    const clientId = String(access.clientId || '').trim();
    if (!accessTokenId || !formId || !clientId) return null;

    const status: PortalFormRequestStatus = access.status === 'revoked'
        ? 'cancelled'
        : access.status === 'submitted' || Boolean(access.submittedAt || access.submissionId)
            ? 'completed'
            : access.status === 'submitting' || Boolean(access.draftUpdatedAt || access.draftAnswers?.length)
                ? 'in_progress'
                : 'pending';

    return {
        id: `email-${accessTokenId}`,
        tenantId: options.tenantId,
        trackingSource: 'email_invite',
        accessTokenId,
        formId,
        formSlug: access.formSlug || options.form?.slug,
        formName: access.formName || options.form?.name || 'Portal form',
        formDescription: access.formDescription || options.form?.description || options.form?.introText,
        formKind: access.formKind || options.form?.kind,
        clientId,
        clientName: access.clientName || access.recipientName || access.recipientEmail || 'Client',
        projectId: access.projectId,
        status,
        draftAnswers: access.draftAnswers,
        draftUpdatedAt: access.draftUpdatedAt,
        openedAt: access.lastOpenedAt,
        submissionId: access.submissionId,
        sentAt: access.createdAt,
        completedAt: access.submittedAt,
        sentBy: access.createdBy,
        sentByName: access.createdByName,
        createdAt: access.createdAt,
        updatedAt: access.updatedAt,
    };
}

export function isOpenPortalFormRequest(status?: PortalFormRequestStatus | null): boolean {
    return status === 'pending' || status === 'in_progress';
}

export function isInProgressPortalFormRequest(request?: Partial<PortalFormRequest> | null): boolean {
    return request?.status === 'in_progress' && Array.isArray(request.draftAnswers);
}

export function refreshedPortalFormRequestStatus(
    status?: PortalFormRequestStatus | null,
): Extract<PortalFormRequestStatus, 'pending' | 'in_progress'> {
    return status === 'in_progress' ? 'in_progress' : 'pending';
}

export function resolvePortalFormDraftAnswers(
    request: Partial<PortalFormRequest> | null | undefined,
    localAnswers: FormSubmissionAnswer[],
): FormSubmissionAnswer[] {
    const hasServerDraft = request?.status === 'in_progress'
        || Boolean(request?.draftUpdatedAt)
        || Boolean(request?.startedAt);

    if (hasServerDraft) {
        return Array.isArray(request?.draftAnswers) ? request.draftAnswers : [];
    }

    return localAnswers;
}
